import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [alreadyVerified, setAlreadyVerified] = useState(false);
  const [error, setError] = useState("");
  const [userInfo, setUserInfo] = useState({ email: "", name: "" });
  
  // Use sessionStorage to prevent double API calls across re-renders
  const getVerificationKey = (token) => `verification_attempted_${token}`;

  useEffect(() => {
    const token = searchParams.get("token");
    
    if (!token) {
      setVerifying(false);
      setError("Invalid verification link");
      return;
    }
    
    // Check if we already attempted verification for this token in this session
    const verificationKey = getVerificationKey(token);
    if (sessionStorage.getItem(verificationKey)) {
      // Already attempted, check the result
      const storedResult = sessionStorage.getItem(`${verificationKey}_result`);
      if (storedResult) {
        const result = JSON.parse(storedResult);
        setSuccess(result.success);
        setAlreadyVerified(result.alreadyVerified);
        setError(result.error);
        setUserInfo(result.userInfo || { email: "", name: "" });
        setVerifying(false);
      }
      return;
    }
    
    // Mark as attempted before making the call
    sessionStorage.setItem(verificationKey, 'true');
    verifyEmailToken(token, verificationKey);
  }, [searchParams]);

  const verifyEmailToken = async (token, verificationKey) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/verify-email/${token}`);
      
      const result = {
        success: !response.data.already_verified,
        alreadyVerified: response.data.already_verified || false,
        error: "",
        userInfo: {
          email: response.data.email || "",
          name: response.data.name || ""
        }
      };
      
      // Store result for potential re-renders
      sessionStorage.setItem(`${verificationKey}_result`, JSON.stringify(result));
      
      if (response.data.already_verified) {
        setAlreadyVerified(true);
        setSuccess(false);
        setError("");
      } else {
        setSuccess(true);
        setAlreadyVerified(false);
        setError("");
        setUserInfo({
          email: response.data.email || "",
          name: response.data.name || ""
        });
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Verification failed. The link may be invalid or expired.";
      const result = {
        success: false,
        alreadyVerified: false,
        error: errorMsg,
        userInfo: { email: "", name: "" }
      };
      
      // Store error result
      sessionStorage.setItem(`${verificationKey}_result`, JSON.stringify(result));
      
      setSuccess(false);
      setAlreadyVerified(false);
      setError(errorMsg);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-[#1E1E1E] dark:to-[#252525] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="/niello-logo.png" 
            alt="Think Niello" 
            className="h-20 mx-auto mb-6"
          />
        </div>

        <Card className="shadow-xl border-0">
          <CardHeader className="text-center">
            {verifying && (
              <>
                <div className="flex justify-center mb-4">
                  <Loader2 className="w-16 h-16 text-[#7a1d42] animate-spin" />
                </div>
                <CardTitle className="text-2xl">Verifying Email...</CardTitle>
                <CardDescription>Please wait while we verify your email address</CardDescription>
              </>
            )}

            {!verifying && success && (
              <>
                <div className="flex justify-center mb-4">
                  <CheckCircle className="w-16 h-16 text-green-500" />
                </div>
                <CardTitle className="text-2xl text-green-600">Email Verified!</CardTitle>
                <CardDescription>Your account has been successfully verified</CardDescription>
              </>
            )}

            {!verifying && alreadyVerified && (
              <>
                <div className="flex justify-center mb-4">
                  <CheckCircle className="w-16 h-16 text-blue-500" />
                </div>
                <CardTitle className="text-2xl text-blue-600">Already Verified</CardTitle>
                <CardDescription>This email address has already been verified</CardDescription>
              </>
            )}

            {!verifying && error && (
              <>
                <div className="flex justify-center mb-4">
                  <XCircle className="w-16 h-16 text-red-500" />
                </div>
                <CardTitle className="text-2xl text-red-600">Verification Failed</CardTitle>
                <CardDescription>{error}</CardDescription>
              </>
            )}
          </CardHeader>

          <CardContent className="space-y-4">
            {success && !error && (
              <>
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <p className="text-green-800 dark:text-green-200 text-sm">
                    <strong>Welcome, {userInfo.name}!</strong>
                  </p>
                  <p className="text-green-700 dark:text-green-300 text-sm mt-1">
                    {userInfo.email}
                  </p>
                  <p className="text-green-600 dark:text-green-400 text-sm mt-2">
                    Your account is now active and you can log in to the Niello Helpdesk.
                  </p>
                </div>

                <Link to="/login">
                  <Button className="w-full bg-[#7a1d42] hover:bg-[#5a1531] text-white">
                    Go to Login
                  </Button>
                </Link>
              </>
            )}

            {alreadyVerified && !error && (
              <>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <p className="text-blue-800 dark:text-blue-200 text-sm">
                    Your email has already been verified. You can log in to your account.
                  </p>
                </div>

                <Link to="/login">
                  <Button className="w-full bg-[#7a1d42] hover:bg-[#5a1531] text-white">
                    Go to Login
                  </Button>
                </Link>
              </>
            )}

            {error && !success && !alreadyVerified && (
              <>
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <p className="text-red-800 dark:text-red-200 text-sm font-medium mb-2">
                    Possible reasons:
                  </p>
                  <ul className="text-red-700 dark:text-red-300 text-sm space-y-1 list-disc list-inside">
                    <li>The verification link has expired (24 hour limit)</li>
                    <li>The link was already used to verify this account</li>
                    <li>The link is invalid or malformed</li>
                  </ul>
                  <p className="text-red-800 dark:text-red-200 text-sm font-medium mt-3 mb-1">
                    What to do:
                  </p>
                  <ul className="text-red-700 dark:text-red-300 text-sm space-y-1 list-disc list-inside">
                    <li>If you already have an account, try logging in</li>
                    <li>If you need a new account, register again to get a new verification email</li>
                    <li>Contact support@niello.com if you continue to have issues</li>
                  </ul>
                </div>

                <div className="flex flex-col gap-2">
                  <Link to="/login">
                    <Button className="w-full bg-[#7a1d42] hover:bg-[#5a1531] text-white">
                      Go to Login
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button variant="outline" className="w-full">
                      Register New Account
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-slate-60 dark:text-white0 dark:text-white">
          <p>Need help? Contact support@niello.com</p>
        </div>
      </div>
    </div>
  );
}
